import { Link } from "react-router-dom";
import { Lock, SearchX, TriangleAlert } from "lucide-react";

export const EmptyState = ({ icon: Icon = SearchX, title, hint, testId }) => (
  <div
    data-testid={testId || "empty-state"}
    className="flex flex-col items-center justify-center gap-2 border border-dashed border-slate-300 bg-white px-6 py-16 text-center"
  >
    <Icon className="h-6 w-6 text-slate-400" strokeWidth={1.5} />
    <p className="font-display text-base font-semibold text-slate-800">{title}</p>
    {hint && <p className="max-w-md text-sm text-slate-500">{hint}</p>}
  </div>
);

export const ErrorState = ({ message }) => (
  <div
    data-testid="error-state"
    className="flex items-start gap-2 border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800"
  >
    <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
    <span>{message}</span>
  </div>
);

// Tone-down palette: warm, low-saturation, no border, so chips read as gentle
// tags rather than buttons. Preserves scannability without shouting.
const BADGE_TONES = {
  slate: "bg-slate-50 text-slate-600",
  blue: "bg-blue-50 text-blue-700",
  emerald: "bg-emerald-50 text-emerald-700",
  amber: "bg-amber-50 text-amber-800",
  rose: "bg-rose-50 text-rose-700",
  violet: "bg-violet-50 text-violet-700",
  indigo: "bg-indigo-50 text-indigo-700",
  teal: "bg-teal-50 text-teal-700",
  orange: "bg-orange-50 text-orange-800",
  pink: "bg-pink-50 text-pink-700",
};

/** Soft pill-style badge. `width="quota"` keeps quota chips the same size across rows. */
export const Badge = ({ children, tone = "slate", width }) => {
  const widthCls = width === "quota" ? "min-w-[92px] text-center" : width === "category" ? "min-w-[54px] text-center" : "";
  return (
    <span
      className={`inline-flex items-center justify-center px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider leading-tight ${BADGE_TONES[tone] || BADGE_TONES.slate} ${widthCls}`}
    >
      {children}
    </span>
  );
};

// KEA raw category code → colour tone. Prefix-based so new codes still get a sensible colour.
const _categoryTone = (code) => {
  if (!code) return "slate";
  const c = String(code).toUpperCase().trim();
  if (c === "NRI") return "amber";
  if (c === "MNG") return "violet";
  if (c === "OPN" || c === "OPNH" || c === "MO") return "emerald";
  if (["MU", "MC", "MA"].includes(c) || c.startsWith("RC")) return "pink";
  if (c.startsWith("GM")) return "blue";
  if (c.startsWith("PGM") || c.startsWith("P1") || c.startsWith("P2") || c.startsWith("P3") || c.startsWith("PST")) return "indigo";
  if (c.startsWith("ST")) return "orange";
  if (c.startsWith("S1") || c.startsWith("S2") || c.startsWith("S3")) return "teal";
  return "slate"; // OBC 1G/2AG/2BG/3AG/3BG etc.
};

/** Category-code chip. Colour is inferred from the raw KEA code so new codes still look right. */
export const CategoryBadge = ({ code }) => {
  if (!code) return <span className="text-xs text-slate-400">NA</span>;
  return (
    <Badge tone={_categoryTone(code)} width="category">
      {code}
    </Badge>
  );
};

export const TrialBanner = ({ total, visible }) => (
  <div
    data-testid="trial-lock-banner"
    className="flex flex-wrap items-center justify-between gap-3 border border-amber-300 bg-amber-50 px-4 py-3"
  >
    <div className="flex items-center gap-2 text-sm text-amber-900">
      <Lock className="h-4 w-4" />
      <span>
        Trial view — showing <strong>{visible}</strong> of{" "}
        <strong className="font-mono-num">{total.toLocaleString("en-IN")}</strong> matching rows.
      </span>
    </div>
    <Link
      data-testid="banner-upgrade-link"
      to="/app/upgrade"
      className="border border-[#1A365D] bg-[#1A365D] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#122845]"
    >
      Unlock MasterPlan ₹999 <span className="ml-1 font-normal text-white/60 line-through">₹4,999</span>
    </Link>
  </div>
);

/**
 * columns: [{ key, label, align, render, sortable, width, minWidth, muted, highlight, mono }]
 *  - `width` / `minWidth` are CSS lengths applied to the header + cells for stable widths.
 *  - `muted` renders the cell in low-emphasis grey mono (year, round, body).
 *  - `highlight` renders the cell as the primary metric (bold, dark, tabular).
 */
export const DataTable = ({
  columns,
  rows,
  sort,
  onSort,
  testId = "data-table",
  rowKey = (_, i) => i,
  rowActions,
}) => (
  <div className="max-h-[62vh] overflow-auto border border-slate-200 bg-white">
    <table className="data-table" data-testid={testId}>
      <thead>
        <tr>
          {columns.map((c) => (
            <th
              key={c.key}
              data-testid={`th-${c.key}`}
              onClick={() => c.sortable !== false && onSort?.(c.key)}
              style={{ width: c.width, minWidth: c.minWidth }}
              className={c.align === "right" ? "text-right" : ""}
            >
              {c.label}
              {sort?.by === c.key ? (sort.dir === 1 ? " ▲" : " ▼") : ""}
            </th>
          ))}
          {rowActions && <th key="__act">Actions</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={rowKey(r, i)} data-testid={`row-${i}`}>
            {columns.map((c) => {
              const classes = [
                c.align === "right" ? "text-right" : "",
                c.mono ? "font-mono-num" : "",
                c.muted ? "col-muted" : "",
                c.highlight ? "col-highlight" : "",
              ]
                .filter(Boolean)
                .join(" ");
              return (
                <td
                  key={c.key}
                  className={classes}
                  style={{ width: c.width, minWidth: c.minWidth }}
                >
                  {c.render ? c.render(r) : (r[c.key] ?? "—")}
                </td>
              );
            })}
            {rowActions && <td>{rowActions(r, i)}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
